import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { clearCustomAuthTokens, getCustomAuthTokens } from "../features/auth/LoginBox";
import { refreshAccessToken } from "../utils/refreshAccessToken";
import { emitSessionExpired } from "../utils/sessionEvents";
import { isJwtExpired } from "../utils/jwtUtils";

const SESSION_CHECK_INTERVAL = 60_000; // 60 seconds

export const useSessionValidator = () => {
  const auth = useAuth();
  const isChecking = useRef(false);

  useEffect(() => {
    if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === "true") return;

    const validateSession = async () => {
      // Support both OIDC auth users and custom auth users (email/password login).
      // Custom auth users have auth.user === null since they bypassed OIDC.
      const customTokens = getCustomAuthTokens();
      const accessToken = auth.user?.access_token ?? customTokens?.access_token ?? null;

      if (!accessToken) return; // not authenticated via either path
      if (isChecking.current) return;
      isChecking.current = true;
      console.log("[AUTH][datasurfr][useSessionValidator.ts] Session check triggered | interval: 60s | visibilityState:", document.visibilityState);
      try {
        const baseUrl = import.meta.env.VITE_API_INTERNAL_URL;
        if (!baseUrl) {
          console.error("VITE_API_INTERNAL_URL is not configured — skipping session validation");
          return;
        }
        const res = await fetch(`${baseUrl}/api/apps/userInfo`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) {
          console.warn("[AUTH][datasurfr][useSessionValidator.ts] Session check non-ok | status:", res.status, "| url:", `${baseUrl}/api/apps/userInfo`);

          if (res.status === 401) {
            // [AUTH] access token may be expired — try to refresh before logging out
            console.log("[AUTH][datasurfr][useSessionValidator.ts] 401 on session check — attempting token refresh");
            let newToken: string | null = null;
            try {
              newToken = await refreshAccessToken();
            } catch {
              // Refresh endpoint returned 5xx or network error.
              // If the access token is also expired locally, the user cannot recover —
              // show the session-expired modal. Otherwise it's a true transient failure.
              if (isJwtExpired(accessToken)) {
                console.warn("[AUTH][datasurfr][useSessionValidator.ts] Refresh failed (5xx/network) AND access token is locally expired — emitting session-expired");
                clearCustomAuthTokens();
                auth.removeUser();
                emitSessionExpired("expired");
              } else {
                console.warn("[AUTH][datasurfr][useSessionValidator.ts] Refresh failed with transient error — access token still valid, keeping session alive");
              }
              return;
            }

            if (newToken) {
              // [AUTH] refresh succeeded — re-run session check with new token
              console.log("[AUTH][datasurfr][useSessionValidator.ts] Token refreshed — re-checking session with new token");
              const retryRes = await fetch(`${baseUrl}/api/apps/userInfo`, {
                credentials: "include",
                headers: { Authorization: `Bearer ${newToken}` },
              });

              if (retryRes.ok) {
                console.log("[AUTH][datasurfr][useSessionValidator.ts] Session check passed after token refresh — user stays logged in");
                return; // access token was the only issue, session is alive
              }

              if (retryRes.status !== 401) {
                // Refresh succeeded but the retry hit a transient non-401 (400/5xx).
                // The freshly-minted token wasn't rejected — this is infra noise, not an
                // expired session. Keep the user logged in (same rule as the outer branch).
                console.warn("[AUTH][datasurfr][useSessionValidator.ts] Non-401 after refresh — transient, keeping session alive | status:", retryRes.status);
                return;
              }

              // Still 401 even with the fresh token — the session is genuinely rejected.
              console.warn("[AUTH][datasurfr][useSessionValidator.ts] Still 401 after refresh — session truly expired");
            } else {
              console.warn("[AUTH][datasurfr][useSessionValidator.ts] Token refresh returned null — refresh token also expired");
            }

            // [AUTH] 401 confirmed and refresh also failed — session is truly expired
            console.warn("[AUTH][datasurfr][useSessionValidator.ts] Clearing custom auth tokens");
            clearCustomAuthTokens();
            console.warn("[AUTH][datasurfr][useSessionValidator.ts] Removing OIDC user");
            auth.removeUser();
            console.warn("[AUTH][datasurfr][useSessionValidator.ts] Emitting session-expired | reason: session fully expired");
            emitSessionExpired("expired");
          } else {
            // Non-401 error (400, 403, 5xx, etc.) — treat as transient infrastructure issue.
            // Do NOT logout: a Tomcat 400 (header size), a 503, or a momentary 5xx is not
            // evidence the session is invalid. Only 401 confirms the token was rejected.
            console.warn("[AUTH][datasurfr][useSessionValidator.ts] Non-401 error on session check — treating as transient, keeping session alive | status:", res.status);
          }
        }
      } catch {
        console.warn("[AUTH][datasurfr][useSessionValidator.ts] Session validation failed — network error (no logout, transient)");
      } finally {
        isChecking.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") validateSession();
    };
    const handleFocus = () => validateSession();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    const interval = setInterval(validateSession, SESSION_CHECK_INTERVAL);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      clearInterval(interval);
    };
  }, [auth]);
};
